// Web-specific implementation of GrowthLineChart using Recharts.
// Vite resolves .web.tsx before .tsx, so this file is used on web and the
// react-native-svg version (GrowthLineChart.tsx) is used on mobile.
import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Dot,
} from 'recharts';
import { GrowthDataDto } from '../storage/WellCheckStorage';

interface GrowthLineChartProps {
  data: GrowthDataDto[];
  heightLabel: string;
  weightLabel: string;
  /** ISO date string (YYYY-MM-DD) of the child's date of birth. Used to compute
   *  age-at-measurement so the X axis shows age rather than calendar dates. */
  dob?: string;
}

/** Returns age in decimal months between two ISO date strings. */
function ageMonths(dob: string, measureDate: string): number {
  const birth = new Date(dob);
  const measured = new Date(measureDate);
  const years = measured.getFullYear() - birth.getFullYear();
  const months = measured.getMonth() - birth.getMonth();
  const days = (measured.getDate() - birth.getDate()) / 30.4;
  return Math.max(0, years * 12 + months + days);
}

/** Formats decimal months into a readable age label. */
function formatAge(months: number): string {
  if (months < 24) {
    return `${Math.round(months)}mo`;
  }
  const years = months / 12;
  const wholeYears = Math.floor(years);
  const remMonths = Math.round((years - wholeYears) * 12);
  return remMonths > 0 ? `${wholeYears}y ${remMonths}mo` : `${wholeYears}y`;
}

export function GrowthLineChart({ data, heightLabel, weightLabel, dob }: GrowthLineChartProps) {
  const sorted = [...data]
    .filter(d => d.height && d.weight && d.date)
    .sort((a, b) => a.date.localeCompare(b.date));

  if (sorted.length < 2) {
    return (
      <div style={{ height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#aaa', fontSize: 13 }}>Not enough data to display chart</span>
      </div>
    );
  }

  const useDob = dob && dob.length >= 8;

  const chartData = sorted.map(d => ({
    // ageMonthsVal is always populated; used as the numeric X value so spacing
    // is proportional to real time. Falls back to index*1 when DOB is absent.
    ageMonthsVal: useDob ? ageMonths(dob!, d.date) : sorted.indexOf(d),
    height: parseFloat(d.height) || 0,
    weight: parseFloat(d.weight) || 0,
  }));

  const xAxisLabel = useDob ? 'Age' : 'Date';

  // Build tick formatter: age labels when DOB known, M/D dates otherwise.
  const tickFormatter = useDob
    ? (val: number) => formatAge(val)
    : (_val: number, index: number) => {
        const parts = sorted[index]?.date.split('-') ?? [];
        return parts.length >= 3
          ? `${parseInt(parts[1], 10)}/${parseInt(parts[2], 10)}`
          : (sorted[index]?.date ?? '');
      };

  // Compute a clean domain with a little padding so first/last dots aren't clipped.
  const xValues = chartData.map(d => d.ageMonthsVal);
  const xMin = Math.min(...xValues);
  const xMax = Math.max(...xValues);
  const xPad = (xMax - xMin) * 0.04 || 1;

  return (
    <div style={{ marginLeft: -8, marginRight: -8 }}>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={chartData} margin={{ top: 15, right: 8, bottom: 10, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="ageMonthsVal"
            type="number"
            scale="linear"
            domain={[xMin - xPad, xMax + xPad]}
            tickFormatter={tickFormatter}
            tick={{ fontSize: 10, fill: '#888' }}
            tickCount={6}
          />
          <YAxis yAxisId="height" orientation="left" tick={{ fontSize: 10, fill: '#3498db' }} />
          <YAxis yAxisId="weight" orientation="right" tick={{ fontSize: 10, fill: '#e67e22' }} />
          <Tooltip formatter={(value, name) => [value, name]} />
          <Legend
            wrapperStyle={{ fontSize: 11 }}
            iconSize={10}
          />
          <Line
            yAxisId="height"
            type="monotone"
            dataKey="height"
            name={`Height (${heightLabel})`}
            stroke="#3498db"
            strokeWidth={2}
            dot={<Dot r={4} fill="#3498db" stroke="#fff" strokeWidth={1.5} />}
            activeDot={{ r: 5 }}
          />
          <Line
            yAxisId="weight"
            type="monotone"
            dataKey="weight"
            name={`Weight (${weightLabel})`}
            stroke="#e67e22"
            strokeWidth={2}
            dot={<Dot r={4} fill="#e67e22" stroke="#fff" strokeWidth={1.5} />}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
